import React, { useState, useEffect } from 'react';
import {
  Phone, Plus, Edit2, Trash2, User, Save, X,
  Sparkles, Zap, Star, PhoneCall, Users, Building2
} from 'lucide-react';
import { voipService } from '../../services/voipService';

const SpeedDials = () => {
  const [speedDials, setSpeedDials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    position: '',
    name: '',
    number: '',
    icon: 'user'
  });

  useEffect(() => {
    loadSpeedDials();
  }, []);

  const loadSpeedDials = async () => {
    try {
      setLoading(true);
      const data = await voipService.getSpeedDials();
      setSpeedDials(data);
    } catch (error) {
      console.error('Error loading speed dials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await voipService.createSpeedDial(addForm);
      setShowAddModal(false);
      setAddForm({ position: '', name: '', number: '', icon: 'user' });
      loadSpeedDials();
    } catch (error) {
      console.error('Error creating speed dial:', error);
      alert(error.response?.data?.detail || 'Failed to create speed dial');
    }
  };

  const handleEdit = (speedDial) => {
    setEditingId(speedDial.id);
    setEditForm({
      name: speedDial.name,
      number: speedDial.number,
      icon: speedDial.icon || 'user'
    });
  };

  const handleSaveEdit = async (id) => {
    try {
      await voipService.updateSpeedDial(id, editForm);
      setEditingId(null);
      setEditForm({});
      loadSpeedDials();
    } catch (error) {
      console.error('Error updating speed dial:', error);
      alert('Failed to update speed dial');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this speed dial?')) {
      return;
    }

    try {
      await voipService.deleteSpeedDial(id);
      loadSpeedDials();
    } catch (error) {
      console.error('Error deleting speed dial:', error);
      alert('Failed to delete speed dial');
    }
  };

  const handleCall = async (speedDial) => {
    try {
      // Get user's extension
      const extension = await voipService.getMyExtension();
      await voipService.makeCall(extension.id, speedDial.number, speedDial.contact_id);
      alert(`Calling ${speedDial.name}...`);
    } catch (error) {
      console.error('Error making call:', error);
      alert('Failed to initiate call');
    }
  };

  const availablePositions = Array.from({ length: 99 }, (_, i) => i + 1)
    .filter(pos => !speedDials.some(sd => sd.position === pos));

  // Quick stats
  const totalSpeedDials = speedDials.length;
  const linkedContacts = speedDials.filter(sd => sd.contact_id).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gradient-to-br from-slate-900 via-slate-900/30 to-slate-900 rounded-xl">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-slate-900 via-slate-900/30 to-slate-900 rounded-xl p-6 overflow-auto">
      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-800/50 backdrop-blur rounded-xl p-4 border border-cyan-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{totalSpeedDials}</div>
              <div className="text-xs text-cyan-400">Speed Dials</div>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 backdrop-blur rounded-xl p-4 border border-cyan-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-rose-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{linkedContacts}</div>
              <div className="text-xs text-cyan-400">Linked Contacts</div>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 backdrop-blur rounded-xl p-4 border border-cyan-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/30">
              <Star className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{99 - totalSpeedDials}</div>
              <div className="text-xs text-cyan-400">Available Slots</div>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Zap className="w-6 h-6 text-cyan-400" />
            Speed Dials
          </h2>
          <p className="text-cyan-400">Quick access to frequently called numbers</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl hover:from-cyan-500 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Speed Dial
        </button>
      </div>

      {/* Speed Dial Grid */}
      {speedDials.length === 0 ? (
        <div className="text-center py-16 bg-slate-800/30 rounded-xl border border-cyan-500/20">
          <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Phone className="w-10 h-10 text-cyan-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No Speed Dials Yet</h3>
          <p className="text-cyan-400 mb-6">Add speed dials for quick access to frequently called numbers</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl hover:from-cyan-500 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25"
          >
            Add Your First Speed Dial
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {speedDials.map((speedDial) => (
            <div
              key={speedDial.id}
              className="bg-slate-800/50 backdrop-blur rounded-xl border border-cyan-500/20 overflow-hidden hover:border-cyan-500/40 transition-all group"
            >
              {editingId === speedDial.id ? (
                // Edit Mode
                <div className="p-4">
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Name"
                    className="w-full mb-2 px-3 py-2 bg-slate-700/50 border border-cyan-500/30 rounded-xl text-white placeholder-cyan-400/50 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <input
                    type="tel"
                    value={editForm.number}
                    onChange={(e) => setEditForm({ ...editForm, number: e.target.value })}
                    placeholder="Phone Number"
                    className="w-full mb-3 px-3 py-2 bg-slate-700/50 border border-cyan-500/30 rounded-xl text-white placeholder-cyan-400/50 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSaveEdit(speedDial.id)}
                      className="flex-1 px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-500 hover:to-emerald-500 transition-all flex items-center justify-center gap-1 shadow-lg shadow-green-500/20"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex-1 px-3 py-2 bg-slate-700/50 text-cyan-300 rounded-xl hover:bg-slate-700 transition-all flex items-center justify-center gap-1"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // Display Mode
                <>
                  <div className="bg-gradient-to-br from-cyan-600 via-cyan-700 to-blue-600 p-6 text-white relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                    <div className="relative">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center text-2xl font-bold shadow-lg">
                          {speedDial.position}
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleEdit(speedDial)}
                            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(speedDial.id)}
                            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold truncate">{speedDial.name}</h3>
                      <p className="text-white/80">{speedDial.number}</p>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <button
                      onClick={() => handleCall(speedDial)}
                      className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 shadow-lg shadow-green-500/20"
                    >
                      <PhoneCall className="w-5 h-5" />
                      Call Now
                    </button>
                    {speedDial.contact_id && (
                      <a href={`/contacts/${speedDial.contact_id}`}
                        className="flex items-center justify-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 py-2 bg-slate-700/30 hover:bg-slate-700/50 rounded-xl transition-all"
                      >
                        <User className="w-4 h-4" />
                        View Contact
                      </a>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-cyan-500/30 rounded-2xl shadow-2xl shadow-cyan-500/10 max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-cyan-500/20">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-cyan-400" />
                Add Speed Dial
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAdd} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-cyan-300 mb-2">
                  Position (1-99)
                </label>
                <select
                  value={addForm.position}
                  onChange={(e) => setAddForm({ ...addForm, position: parseInt(e.target.value) })}
                  required
                  className="w-full px-4 py-3 bg-slate-700/50 border border-cyan-500/30 rounded-xl text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="">Select position...</option>
                  {availablePositions.slice(0, 20).map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-cyan-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={addForm.name}
                  onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  required
                  placeholder="e.g., John Doe, Home Office"
                  className="w-full px-4 py-3 bg-slate-700/50 border border-cyan-500/30 rounded-xl text-white placeholder-cyan-400/50 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-cyan-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={addForm.number}
                  onChange={(e) => setAddForm({ ...addForm, number: e.target.value })}
                  required
                  placeholder="e.g., +1 (555) 123-4567"
                  className="w-full px-4 py-3 bg-slate-700/50 border border-cyan-500/30 rounded-xl text-white placeholder-cyan-400/50 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 bg-slate-700/50 text-cyan-300 rounded-xl hover:bg-slate-700 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl hover:from-cyan-500 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25"
                >
                  Add Speed Dial
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpeedDials;
